/**
 * How much work a frame is allowed to be, decided once at startup.
 *
 * Written after the same build ran at a comfortable frame rate on a desktop with
 * a discrete card and at something between sluggish and unplayable on a Surface
 * and on a five-year-old MacBook Pro. Nothing about those machines was broken.
 * They were being asked to shade five and a half million pixels a frame through
 * a fourteen-light physically-based material with soft shadows and a five-mip
 * bloom, which is a reasonable thing to ask of a 3080 and is not a reasonable
 * thing to ask of Iris Xe.
 *
 * The tiers below are mostly about **pixels and lights**, in that order, because
 * that is where the cost is. Everything a fragment shader does, it does per
 * pixel: halving the pixel ratio quarters the work, and every torch removed from
 * the budget shortens the loop that every lit pixel in the arena runs. Neither
 * changes what the game *is* — the arena is dark and foggy and the silhouettes
 * are what matter, and none of that depends on shading it at native resolution
 * on a 2880-wide panel.
 *
 * Detection is a guess and is meant to be overridden. It looks at what the
 * driver says it is and how many pixels it is being asked to fill, gets it right
 * often enough to matter for somebody who will never open a settings menu, and
 * loses nothing when it is wrong because the choice is remembered the moment
 * anybody makes one.
 */

export type Tier = 'low' | 'medium' | 'high';

export interface QualitySettings {
  tier: Tier;
  /**
   * Ceiling on `devicePixelRatio`.
   *
   * The single biggest lever there is, and the one most worth understanding: a
   * Retina or Surface panel reports 2, so the renderer draws four times the
   * pixels of the same window at 1. Capping at 1 on a weak GPU is not "low
   * resolution" — it is the resolution the window actually occupies, drawn once
   * per pixel instead of four times.
   */
  pixelRatio: number;
  /**
   * How many torch fixtures are lit at once.
   *
   * Every lit material in the scene carries a loop over every light, so this is
   * paid by the whole arena on every pixel. Fixtures beyond the budget are still
   * drawn as mesh with fire on them; they just do not illuminate.
   */
  torchLights: number;
  shadows: boolean;
  /** Square, per side. */
  shadowMapSize: number;
  /** `PCFSoft` costs several taps a pixel more than `PCF`. */
  shadowSoft: boolean;
  /** Frames between shadow-map redraws. 1 is every frame. */
  shadowInterval: number;
  /** Half-width of the shadow frustum, metres. */
  shadowExtent: number;
  bloom: boolean;
}

export const TIERS: Record<Tier, QualitySettings> = {
  /*
   * Integrated graphics, and the honest answer for them.
   *
   * Pixel ratio 1 and six lights together take the per-frame fragment cost to
   * somewhere around a sixth of high. Shadows stay on — a dark arena with no
   * shadows at all reads as flat and is the thing people notice first — but at
   * a quarter of the map area, hard-edged, and redrawn every third frame. A
   * shadow twelve centimetres behind a walking mage is not visible; no shadow
   * under them at all is.
   *
   * Bloom is the one thing removed outright. It is ten-odd fullscreen passes
   * and it is decoration: the spells are additive and bright without it.
   */
  low: {
    tier: 'low',
    pixelRatio: 1,
    torchLights: 6,
    shadows: true,
    shadowMapSize: 1024,
    shadowSoft: false,
    shadowInterval: 3,
    shadowExtent: 26,
    bloom: false,
  },
  medium: {
    tier: 'medium',
    pixelRatio: 1.25,
    torchLights: 10,
    shadows: true,
    shadowMapSize: 2048,
    shadowSoft: false,
    shadowInterval: 2,
    shadowExtent: 32,
    bloom: true,
  },
  high: {
    tier: 'high',
    pixelRatio: 2,
    torchLights: 14,
    shadows: true,
    shadowMapSize: 2048,
    shadowSoft: true,
    shadowInterval: 1,
    shadowExtent: 32,
    bloom: true,
  },
};

const STORE_KEY = 'mw.quality';

/**
 * What the driver calls itself, past the browser's generic string.
 *
 * `RENDERER` alone returns something deliberately uninformative on most
 * browsers; the unmasked parameter is the real one and needs an extension that
 * is not always present. A throwaway context because this runs before the game's
 * renderer exists — the tier has to be known first, since the light count is
 * baked into every material at construction and cannot be changed afterwards
 * without recompiling all of them.
 */
export function detectGpu(): string {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') ?? canvas.getContext('webgl');
    if (!gl) return '';
    const dbg = (gl as WebGLRenderingContext).getExtension('WEBGL_debug_renderer_info');
    if (!dbg) return '';
    return String(
      (gl as WebGLRenderingContext).getParameter(
        (dbg as { UNMASKED_RENDERER_WEBGL: number }).UNMASKED_RENDERER_WEBGL,
      ),
    );
  } catch {
    // A context can be refused outright — too many live already, or a machine
    // with hardware acceleration switched off. Not knowing is a fine answer;
    // the caller falls back to counting pixels.
    return '';
  }
}

/** Millions of pixels the renderer would be asked to fill, uncapped. */
export function backbufferMegapixels(): number {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  return (window.innerWidth * dpr * window.innerHeight * dpr) / 1e6;
}

/**
 * A guess at what this machine should be asked to do.
 *
 * The rule is roughly "integrated graphics, or software, or a lot of pixels".
 * Deliberately crude — it exists so somebody who never opens a menu gets a
 * playable first match, not so it can be right about every GPU ever made. The
 * reason is returned alongside so it can be printed rather than wondered about.
 */
export function detectTier(gpu = detectGpu(), pixels = backbufferMegapixels()): {
  tier: Tier; gpu: string; reason: string;
} {
  const name = gpu.toLowerCase();

  // No hardware at all. Anything above the floor here is unplayable.
  if (name.includes('swiftshader') || name.includes('llvmpipe') || name.includes('software')) {
    return { tier: 'low', gpu, reason: 'software rendering' };
  }

  /*
   * Integrated parts, which is what most laptops are.
   *
   * Apple's M-series is included and is the least comfortable call here: an M1
   * is not a weak GPU, but it is invariably driving a Retina panel, which is the
   * half of the problem this is actually detecting. Medium rather than low.
   */
  const apple = /apple m\d/.test(name);
  const integrated = name.includes('intel') || name.includes('iris')
    || name.includes('uhd graphics') || name.includes('hd graphics')
    || name.includes('mali') || name.includes('adreno') || name.includes('powervr');

  if (integrated) {
    return pixels > 2.5
      ? { tier: 'low', gpu, reason: `integrated graphics at ${pixels.toFixed(1)}MP` }
      : { tier: 'medium', gpu, reason: 'integrated graphics' };
  }
  if (apple) {
    return pixels > 5
      ? { tier: 'low', gpu, reason: `Apple silicon at ${pixels.toFixed(1)}MP` }
      : { tier: 'medium', gpu, reason: 'Apple silicon' };
  }

  /*
   * A discrete card whose name says nothing useful, or one nobody told us about.
   *
   * `AMD Radeon Pro` covers the dedicated half of an older MacBook Pro, which is
   * a real GPU driving a Retina panel and belongs at medium rather than high.
   */
  if (name.includes('radeon pro')) {
    return { tier: 'medium', gpu, reason: 'mobile discrete GPU' };
  }
  if (!gpu) {
    // Nothing to go on. Judge by how much it is being asked to fill.
    return pixels > 4
      ? { tier: 'medium', gpu, reason: `unknown GPU at ${pixels.toFixed(1)}MP` }
      : { tier: 'high', gpu, reason: 'unknown GPU' };
  }
  return { tier: 'high', gpu, reason: 'discrete GPU' };
}

/** A stored choice, if the player has ever made one. */
export function storedTier(): Tier | null {
  try {
    const value = window.localStorage.getItem(STORE_KEY);
    return value === 'low' || value === 'medium' || value === 'high' ? value : null;
  } catch {
    // Private browsing, or storage disabled. Detection every time is a fine
    // fallback and is what happens for a first-time player anyway.
    return null;
  }
}

export function storeTier(tier: Tier): void {
  try {
    window.localStorage.setItem(STORE_KEY, tier);
  } catch {
    // Nothing to do about it and nothing worth telling anybody. The setting
    // holds for this session either way.
  }
}

/**
 * The settings to start with: an explicit choice, then a remembered one, then a
 * guess.
 *
 * `?quality=low` wins over everything and is not remembered, because it is a
 * thing somebody types to compare two runs and should not quietly become their
 * setting for ever.
 */
export function startingQuality(param?: string | null): QualitySettings & { reason: string } {
  if (param === 'low' || param === 'medium' || param === 'high') {
    return { ...TIERS[param], reason: 'set by ?quality' };
  }
  const stored = storedTier();
  if (stored) return { ...TIERS[stored], reason: 'your saved setting' };

  const { tier, gpu, reason } = detectTier();
  return { ...TIERS[tier], reason: gpu ? `${reason} — ${gpu}` : reason };
}
